import { useAPIQuery } from "../../../hooks/useAPIQuery";
import { Table } from "@dev2_techwalnut/tw-react-crud-dashlite";
import { useEffect, useMemo, useState } from "react";
import Error from "../../ErrorPages/Error";
import { useNavigate, useSearchParams } from "react-router-dom";
import secureLocalStorage from "react-secure-storage";
import Swal from "sweetalert2";

const RaiseRequisition = () => {
    const navigate = useNavigate();
    const userID = secureLocalStorage.getItem("userID");
    const privileges = secureLocalStorage.getItem("privileges");

    const [searchParams, setSearchParams] = useSearchParams();

    const initialEntries = Number(searchParams.get("entries")) || 10;
    const initialPage = Number(searchParams.get("page")) || 1;
    const initialDate = searchParams.get("date") || null;
    const initialMachine = searchParams.get("machine") || null;

    const [currentDate, setCurrentDate] = useState(initialDate);
    const [currentMachine, setCurrentMachine] = useState(initialMachine);

    const [currentAction, setCurrentAction] = useState("");
    const [raiseForDate, setRaiseForDate] = useState("");
    const [raiseForMachine, setRaiseForMachine] = useState("");

    const [pageIndex, setPageIndex] = useState(initialPage - 1);
    const [entries, setEntries] = useState(initialEntries);
    const [globalFilterValue, setGlobalFilterValue] = useState("");
    const [columnFilterKey, setColumnFilterKey] = useState("");
    const [columnFilterValue, setColumnFilterValue] = useState("");
    const [sorting, setSorting] = useState([]);

    const TypeMasterQuery = useAPIQuery({
        key: "api/type-master",
        endpoint: "/api/type-master/",
    });

    const TypeMasterQueryData = TypeMasterQuery.useFetch({
        params: {
            q: "Issue Slip Status",
            c: "category",
        },
    });

    const { useFetch: useFetchDailyPlan } = useAPIQuery({
        key: "daily-plan",
        endpoint: "/api/daily-plan/",
    });
    const { useCreate: useRaiseRequisition } = useAPIQuery({
        key: "requisition",
        endpoint: "/api/requisitions/",
    });
    const {
        data: dailyPlans,
        refetch: refetchDailyPlans,
        isLoading: dailyPlansLoading,
        isFetching: fetchingDailyPlans,
        isError: errorLoadingDailyPlans,
    } = useFetchDailyPlan({
        params: {
            entries: entries,
            page_no: pageIndex + 1,
            q: globalFilterValue !== "" ? globalFilterValue : columnFilterValue,
            c: globalFilterValue === "" ? columnFilterKey : "",
            action:
                !currentDate && !currentMachine
                    ? "date_view"
                    : currentDate && !currentMachine
                    ? "machine_view"
                    : "daily_plan_view",
            date: currentDate,
            machine_name: currentMachine,
            asc:
                sorting &&
                sorting
                    ?.filter((item) => !item?.desc)
                    ?.map((item) => item?.id)
                    ?.join(","),
            desc:
                sorting &&
                sorting
                    ?.filter((item) => item?.desc)
                    ?.map((item) => item?.id)
                    ?.join(","),
        },
        abortOnNewRequest: true,
        refetchOnWindowFocus: false,
        staleTime: 1000 * 60 * 3,
        retry: 3,
        keepPlaceholder: true,
        refetchOnReconnect: true,
    });
    const { mutate: raiseRequisition } = useRaiseRequisition({
        params: {
            action: currentAction,
            date: raiseForDate,
            machine_id: raiseForMachine,
        },
        errorMessageFromServer: true,
        successMessageFromServer: true,
        onSuccess: () => {
            refetchDailyPlans();
        },
    });
    const handleRaiseRequisition = (row, action) => {
        setCurrentAction(action);
        if (action === "date_wise_requisitions") {
            setRaiseForDate(row?.date);
            setRaiseForMachine("");
        } else if (action === "machine_wise_requisitions") {
            setRaiseForDate(row?.date);
            setRaiseForMachine(row?.machine?.id);
        } else {
            setRaiseForDate("");
            setRaiseForMachine("");
        }
        Swal.fire({
            title: "Do you want to raise the requisition?",
            showDenyButton: true,
            confirmButtonText: "Yes",
            denyButtonText: `No`,
        }).then((result) => {
            if (result.isConfirmed) {
                raiseRequisition({
                    data: {
                        daily_plan_id: row?.id,
                        created_by: userID,
                        updated_by: userID,
                    },
                });
            }
        });
    };

    const handleDateSelection = (date) => {
        setCurrentDate(date);
        setSearchParams((prev) => ({
            ...Object.fromEntries(prev.entries()),
            date: date,
        }));
    };

    const handleMachineSelection = (machine) => {
        setCurrentMachine(machine?.item?.item_name);
        setSearchParams((prev) => ({
            ...Object.fromEntries(prev.entries()),
            machine: machine?.item?.item_name,
        }));
    };

    const columns = [
        {
            accessor: "daily_plan_code",
            header: "Daily Plan",
        },
        {
            accessor: "item",
            header: "Item",
            cell: (row) => <>{row?.item?.item_name}</>,
        },
        {
            accessor: "qty_to_be_produced",
            header: "Quantity To Be Produced",
        },
        {
            accessor: "date",
            header: "Date",
        },
        privileges.includes("raise_requisition_action") && {
            accessor: "raise_requisition",
            header: "Raise Requisition",
            cell: (row) => (
                <button
                    className="btn btn-sm btn-primary"
                    onClick={() =>
                        handleRaiseRequisition(row, "fetch_daily_plan")
                    }
                >
                    Raise
                </button>
            ),
        },
        privileges.includes("view_slip") && {
            accessor: "issue_slip",
            header: "Issue Slip",
            cell: (row) => (
                <button
                    className="btn btn-sm btn-secondary"
                    onClick={() =>
                        navigate("/issue-slip", {
                            state: {
                                data: row,
                                action: "issue_slip_for_daily_plan",
                                date: row?.date,
                                machine: row?.machine?.id,
                            },
                        })
                    }
                >
                    View
                </button>
            ),
        },
    ].filter(Boolean);

    const globalColumns = [
        {
            accessor: "date",
            header: "Date",
            cell: (row) => (
                <span
                    className="link link-primary"
                    style={{ cursor: "pointer" }}
                    onClick={() => handleDateSelection(row?.date)}
                >
                    {row?.date}
                </span>
            ),
            sortable: true,
        },
        privileges.includes("raise_requisition_action") && {
            accessor: "raise_requisition",
            header: "Raise Requisition",
            cell: (row) => (
                <button
                    className="btn btn-sm btn-primary"
                    onClick={() =>
                        handleRaiseRequisition(row, "date_wise_requisitions")
                    }
                >
                    Raise
                </button>
            ),
        },
        privileges.includes("view_slip") && {
            accessor: "issue_slip",
            header: "Issue Slip",
            cell: (row) => (
                <button
                    className="btn btn-sm btn-secondary"
                    onClick={() =>
                        navigate("/issue-slip", {
                            state: {
                                data: row,
                                action: "issue_slip_for_date",
                                date: row?.date,
                                machine: currentMachine,
                            },
                        })
                    }
                >
                    View
                </button>
            ),
        },
    ].filter(Boolean);

    const machineViewColumns = [
        {
            accessor: "machine",
            header: "Machine",
            cell: (row) => (
                <span
                    className="link"
                    style={{
                        cursor: "pointer",
                        pointerEvents: row?.machine?.item?.id ? "" : "none",
                    }}
                    onClick={() => handleMachineSelection(row?.machine)}
                >
                    {row?.machine?.item?.item_name}
                </span>
            ),
        },
        privileges.includes("raise_requisition_action") && {
            accessor: "raise_requisition",
            header: "Raise Requisition",
            cell: (row) => (
                <button
                    className="btn btn-sm btn-primary"
                    onClick={() =>
                        handleRaiseRequisition(row, "machine_wise_requisitions")
                    }
                    disabled={!row?.machine?.item?.id}
                >
                    Raise
                </button>
            ),
        },
        privileges.includes("view_slip") && {
            accessor: "issue_slip",
            header: "Issue Slip",
            cell: (row) => (
                <button
                    className="btn btn-sm btn-secondary"
                    onClick={() =>
                        navigate("/issue-slip", {
                            state: {
                                data: row,
                                action: "issue_slip_for_machine",
                                date: row?.date,
                                machine: row?.machine?.id,
                            },
                        })
                    }
                    disabled={!row?.machine?.item?.id}
                >
                    View
                </button>
            ),
        },
    ].filter(Boolean);

    const handlePagination = (value) => {
        setSearchParams((prev) => ({
            ...Object.fromEntries(prev.entries()),
            entries: value.pageSize,
            page: value.pageIndex + 1,
        }));
        setPageIndex(value.pageIndex);
        setEntries(value.pageSize);
    };
    const handleGlobalFilterChange = (value) => {
        setGlobalFilterValue(value);
    };
    const handleColumnFilterChange = (value) => {
        setColumnFilterKey(
            value
                .filter((item) => item.value)
                .map((item) => item.id)
                .join(",")
        );
        setColumnFilterValue(
            value
                .filter((item) => item.value)
                .map((item) => item.value)
                .join(",")
        );
    };

    const currentColumns = useMemo(() => {
        if (currentDate === null && currentMachine === null) {
            return globalColumns;
        }
        if (currentDate !== null && currentMachine === null) {
            return machineViewColumns;
        }
        return columns;
    }, [currentDate, currentMachine, TypeMasterQueryData?.data]);

    useEffect(() => {
        setPageIndex(initialPage - 1);
        setEntries(initialEntries);
        setCurrentDate(initialDate);
        setCurrentMachine(initialMachine);
    }, [searchParams]);

    const handleSortingChnage = (value) => {
        setSorting(value);
    };

    if (errorLoadingDailyPlans) return <Error />;
    return (
        <>
            <div className="nk-block-head nk-block-head-sm">
                <div className="nk-block-between">
                    <div className="nk-block-head-content d-flex">
                        <h3 className="nk-block-title page-title">
                            Raise Requisition
                        </h3>
                    </div>
                    {currentDate !== null || currentMachine !== null ? (
                        <button
                            className="btn btn-primary"
                            onClick={() => navigate(-1)}
                        >
                            Back
                        </button>
                    ) : null}
                </div>
            </div>
            <Table
                data={
                    dailyPlans
                        ? [
                              ...(dailyPlans?.results?.data ?? []),
                              ...((dailyPlans?.results?.machine ?? []).map(
                                  (m) => ({
                                      machine: {
                                          item: {
                                              item_name: m?.item__item_name,
                                          },
                                      },
                                  })
                              ) ?? []),
                          ]
                        : []
                }
                columns={currentColumns}
                isLoading={dailyPlansLoading || fetchingDailyPlans}
                pagination
                pageIndex={pageIndex}
                pageSize={entries}
                rowCount={dailyPlans ? dailyPlans?.count : 0}
                selectRows={false}
                filterModal={false}
                editRows={false}
                deleteRows={false}
                onPaginationChange={handlePagination}
                onGlobalFilterChange={handleGlobalFilterChange}
                onColumnFilterChange={handleColumnFilterChange}
                onSortingChange={handleSortingChnage}
            />
        </>
    );
};

export default RaiseRequisition;
